/**
 * Thin wrappers around Google Identity Services + Apple's auth.js.
 *
 * The SDKs themselves are loaded via <script> tags in index.html; this module
 * waits for the globals to appear, then exposes Promise-based helpers that
 * resolve with the provider's id_token. The id_token is what our backend's
 * /auth/oauth/{google,apple} endpoints verify.
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID_WEB ?? '';
const APPLE_SERVICES_ID = import.meta.env.VITE_APPLE_SERVICES_ID ?? '';

// ── Readiness helpers ───────────────────────────────────────────────────────
// The SDKs are loaded async/defer. If a user clicks a sign-in button before
// they've finished loading we poll briefly. Times out after 5s.

function waitForGlobal<T>(check: () => T | undefined, timeoutMs = 5000): Promise<T> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      const v = check();
      if (v) return resolve(v);
      if (Date.now() - start > timeoutMs) {
        return reject(new Error('Third-party sign-in script never loaded. Check your network.'));
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

// ── Google ──────────────────────────────────────────────────────────────────
//
// We use the rendered "Sign in with Google" button (Google's official styled
// iframe-embedded button) because it's the only modern GIS flow that returns
// an id_token directly. Custom-styled buttons would force us into the OAuth2
// access-token flow, which doesn't return an id_token without an additional
// /userinfo round-trip — extra complexity for no real win.

let googleInitialized = false;
let googleResolver: ((idToken: string) => void) | null = null;
let googleRejecter: ((err: Error) => void) | null = null;

function ensureGoogleInitialized(): Promise<void> {
  if (googleInitialized) return Promise.resolve();
  if (!GOOGLE_CLIENT_ID) {
    return Promise.reject(new Error('VITE_GOOGLE_CLIENT_ID_WEB is not set.'));
  }
  return waitForGlobal(() => window.google?.accounts?.id).then((gid) => {
    gid.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => {
        if (response.credential && googleResolver) {
          googleResolver(response.credential);
          googleResolver = null;
          googleRejecter = null;
        } else if (googleRejecter) {
          googleRejecter(new Error('No credential returned from Google.'));
          googleResolver = null;
          googleRejecter = null;
        }
      },
    });
    googleInitialized = true;
  });
}

/**
 * Renders Google's official sign-in button into the given DOM node and
 * resolves with the id_token when the user completes the flow.
 *
 * Renders an iframe; styling is Google's, not ours. Theme can be tuned
 * to roughly match the cheeky-warm palette via the options below.
 */
export async function renderGoogleButton(parent: HTMLElement): Promise<string> {
  await ensureGoogleInitialized();
  return new Promise<string>((resolve, reject) => {
    googleResolver = resolve;
    googleRejecter = reject;
    // Clear any previous render so re-mounts don't stack buttons.
    parent.replaceChildren();
    window.google!.accounts.id.renderButton(parent, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'pill',
      logo_alignment: 'left',
      width: parent.clientWidth || 280,
    });
  });
}

// ── Apple ───────────────────────────────────────────────────────────────────
//
// Apple's auth.js supports both popup and full-redirect modes. We use popup
// so the entire flow stays in JS and we don't need a special callback page
// to extract the id_token from a URL hash. (The existing AppleCallback route
// is kept as a fallback for browsers that block third-party popups.)

export async function signInWithApple(): Promise<string> {
  if (!APPLE_SERVICES_ID) {
    throw new Error('VITE_APPLE_SERVICES_ID is not set.');
  }
  const auth = (await waitForGlobal(() => window.AppleID?.auth)) as AppleIDAuth;

  auth.init({
    clientId: APPLE_SERVICES_ID,
    scope: 'name email',
    redirectURI: window.location.origin + '/auth/oauth/apple/callback',
    usePopup: true,
  });

  const response = await auth.signIn();
  const idToken = response.authorization?.id_token;
  if (!idToken) throw new Error('Apple did not return an id_token.');
  return idToken;
}
